import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Sentry } from '../../sentry';

interface Props {
  children: ReactNode;
  /** Rendered in place of the map after a throw. `retry` remounts it. */
  fallback: (retry: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Contains a MapKit failure to the map itself.
 *
 * mapkit-react configures the map inside effects after the async script
 * load; MapKit throws from those setters when the map box is degenerate
 * ("[MapKit] The `scaleFactor` parameter passed to `MapRect.scale()` is
 * not a number", ADR-128). Without this boundary the throw climbs to the
 * route-level ErrorBoundary in DashboardLayout and replaces the WHOLE
 * form with the error screen (2026-09-22, session edit page, live event).
 * The map is decoration on a form; a map failure must not cost the form.
 *
 * Catching here unmounts the <Map> subtree, which detaches the parent
 * ref and runs LocationPicker's deferred destroy, so the failed instance
 * releases its WebGL context like any other unmount. Retry resets state
 * and mounts a fresh map.
 *
 * Reported to Sentry with a surface tag so a rise in map failures stays
 * visible even though the route-level boundary no longer sees them.
 */
export class MapErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    Sentry.captureException(error, {
      tags: { surface: 'location-picker-map' },
      contexts: {
        react: { componentStack: info.componentStack ?? '' },
      },
    });
  }

  retry = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return this.props.fallback(this.retry);
    }
    return this.props.children;
  }
}
